import React from "react";
import { useSelector } from "react-redux";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { PieChartData } from "./PerformanceTypes";
import { selectIsReportAPIAvailable } from "store/performance-report/PerformanceReportSelectors";
import {
  PieChartContainer,
  PieChartTitle,
  PieChartContent,
  PieChartWrapper,
  PieChartLegend,
  LegendItem,
  LegendColor,
  LegendText,
} from "./PerformanceStyles";
import { PerformanceSkeleton } from "./PerformanceSkeleton";

interface PieChartProps {
  data: PieChartData;
  className?: string;
}

const PieChart: React.FC<PieChartProps> = ({ data, className }) => {
  const isReportAPIAvailable = useSelector(selectIsReportAPIAvailable);
  if (!isReportAPIAvailable || data.data.length === 0) {
    return (
      <PieChartContainer className={className}>
        <PieChartTitle>{data.title}</PieChartTitle>
        <PieChartContent>
          <PieChartWrapper>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "160px",
                width: "160px",
              }}
            >
              <i
                className="fas fa-minus"
                style={{ fontSize: "48px", color: "#4D4D4E" }}
              ></i>
            </div>
          </PieChartWrapper>
          <PieChartLegend></PieChartLegend>
        </PieChartContent>
      </PieChartContainer>
    );
  }

  if (data.isLoading) {
    return (
      <PerformanceSkeleton
        type="pie-chart"
        title={data.title}
        legendItems={data.data.length || 4}
      />
    );
  }
  const totalValue =
    data.totalValue || data.data.reduce((sum, item) => sum + item.value, 0);
  const chartData = data.data.map((item) => ({
    ...item,
    percentage:
      item.percentage || (totalValue > 0 ? (item.value / totalValue) * 100 : 0),
  }));
  return (
    <PieChartContainer className={className}>
      <PieChartTitle>{data.title}</PieChartTitle>
      <PieChartContent>
        <PieChartWrapper>
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={0}
                outerRadius={80}
                dataKey="value"
                minAngle={5}
                isAnimationActive={false}
                onClick={() => {}}
                onMouseEnter={() => {}}
                onMouseLeave={() => {}}
                style={{ outline: "none" }}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </RechartsPieChart>
          </ResponsiveContainer>
        </PieChartWrapper>
        <PieChartLegend>
          {chartData.map((item, index) => (
            <LegendItem key={index}>
              <LegendColor color={item.color} />
              <LegendText>{item.name}</LegendText>
            </LegendItem>
          ))}
        </PieChartLegend>
      </PieChartContent>
    </PieChartContainer>
  );
};

export default PieChart;
